import { type FormEvent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { isOnline, subscribeToConnectivity } from '../../offline/connectivity';
import { useOffline } from '../../offline/OfflineProvider';
import { VaultExpiredError, VaultLockedError, VaultPasswordError, type VaultSummary } from '../../offline/vault';
import { BrandLogo } from '../../shared/components/BrandLogo';
import { Icon } from '../../shared/components/Icon';
import { LanguageSelect } from '../../shared/components/LanguageSelect';
import { PoweredBy } from '../../shared/components/ui';
import { getActiveLocale } from '../../shared/i18n/i18n';
import { formatDate, formatRelativeTime } from '../../shared/lib/formatters';
import { useTheme } from '../../shared/theme/theme';
import { useAuth } from './AuthContext';

/**
 * Signing in with no connection.
 *
 * The password is not compared against anything here — there is nothing to compare it to. It is
 * used to derive the key that unwraps the copy, so a wrong password simply fails to decrypt, and
 * the count of failures is what limits guessing.
 *
 * The page states plainly what the operator is getting: figures as of a moment in the past, a
 * window that closes on a date, and writes that will be sent later rather than now.
 */
export function OfflineUnlockPage() {
  const { t } = useTranslation();
  const locale = getActiveLocale();
  const { retryHydration, unlockOffline } = useAuth();
  const { counts, forgetDevice, vault } = useOffline();
  const { theme, toggle: toggleTheme } = useTheme();

  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [errorKey, setErrorKey] = useState<string | null>(null);
  const [waitingSeconds, setWaitingSeconds] = useState(0);
  const [confirmForget, setConfirmForget] = useState(false);

  const lockedForMs = vault?.lockedForMs ?? 0;

  useEffect(() => {
    if (lockedForMs <= 0) {
      setWaitingSeconds(0);
      return undefined;
    }
    setWaitingSeconds(Math.ceil(lockedForMs / 1000));
    const timer = window.setInterval(() => {
      setWaitingSeconds((value) => (value <= 1 ? 0 : value - 1));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [lockedForMs]);

  /*
   * The connection coming back is a better answer than this screen, so the session probe is run
   * again the moment it does.
   *
   * It is deliberately driven by the *transition* rather than by the current state. Firing on mount
   * whenever `online` happened to be true would spin: hydration remounts this page, which would
   * hydrate again. The operator's own "try the connection again" button is the way to force one.
   */
  useEffect(() => subscribeToConnectivity(() => {
    if (isOnline()) void retryHydration();
  }), [retryHydration]);

  const submit = useCallback(async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setErrorKey(null);
    try {
      await unlockOffline(password);
    } catch (error) {
      if (error instanceof VaultLockedError) {
        setWaitingSeconds(Math.ceil(error.retryAfterMs / 1000));
        setErrorKey('offline.unlock.lockedError');
      } else if (error instanceof VaultPasswordError) {
        setErrorKey('offline.unlock.passwordError');
      } else if (error instanceof VaultExpiredError) {
        setErrorKey('offline.unlock.expiredError');
      } else {
        setErrorKey('offline.unlock.failedError');
      }
      setPassword('');
    } finally {
      setSubmitting(false);
    }
  }, [password, unlockOffline]);

  const summary: VaultSummary | null = vault;
  const barred = waitingSeconds > 0;

  return (
    <main className="auth-page">
      <section className="auth-card offline-unlock" aria-labelledby="offline-unlock-title">
        <header className="auth-card__header">
          <Link className="brand__mark" to="/" aria-label={t('app.name')}><BrandLogo /></Link>
          <div>
            <p className="brand-tagline">{t('app.tagline')}</p>
            <p className="eyebrow">{t('offline.unlock.eyebrow')}</p>
            <h1 id="offline-unlock-title">{t('offline.unlock.title')}</h1>
            <p>{t('offline.unlock.subtitle')}</p>
          </div>
          <div className="auth-preferences">
            <LanguageSelect />
            <button
              className="theme-switch"
              type="button"
              onClick={toggleTheme}
              aria-label={theme === 'dark' ? t('theme.switchToLight') : t('theme.switchToDark')}
            >
              <Icon name={theme === 'dark' ? 'sun' : 'moon'} />
            </button>
          </div>
        </header>

        {summary !== null && (
          <dl className="offline-unlock__facts">
            <div>
              <dt>{t('offline.unlock.account')}</dt>
              <dd dir="auto"><strong>{summary.userLabel}</strong></dd>
            </div>
            <div>
              <dt>{t('offline.unlock.copyTaken')}</dt>
              <dd>{formatRelativeTime(summary.lastOnlineAt, locale)}</dd>
            </div>
            <div>
              <dt>{t('offline.unlock.windowCloses')}</dt>
              <dd>{formatDate(summary.expiresAt, locale, true)}</dd>
            </div>
          </dl>
        )}

        <form className="auth-form" onSubmit={(event) => void submit(event)}>
          <label className="field">
            <span>{t('auth.password')}</span>
            <input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              autoComplete="current-password"
              required
              disabled={submitting || barred}
              autoFocus
            />
            <small>{t('offline.unlock.passwordHint')}</small>
          </label>

          {barred && (
            <p className="auth-error" role="alert">
              {t('offline.unlock.barred', { seconds: waitingSeconds })}
            </p>
          )}
          {errorKey !== null && !barred && <p className="auth-error" role="alert">{t(errorKey)}</p>}

          <button className="button button--primary auth-submit" type="submit" disabled={submitting || barred}>
            <Icon name="lock" />
            {submitting ? t('offline.unlock.opening') : t('offline.unlock.action')}
          </button>
        </form>

        <div className="inline-alert offline-unlock__scope">
          <Icon name="cloudOff" />
          {t('offline.unlock.scope')}
        </div>

        <footer className="offline-unlock__footer">
          <button className="text-link" type="button" onClick={() => void retryHydration()}>
            <Icon name="sync" className="icon icon--small" />
            {t('offline.unlock.tryConnection')}
          </button>

          {confirmForget ? (
            <span className="offline-unlock__confirm" role="alert">
              <span>
                {counts.total > 0
                  ? t('offline.forget.confirmWithQueue', { count: counts.total })
                  : t('offline.forget.confirm')}
              </span>
              <button className="text-link text-link--danger" type="button" onClick={() => void forgetDevice()}>
                {t('offline.forget.action')}
              </button>
              <button className="text-link" type="button" onClick={() => setConfirmForget(false)}>{t('common.cancel')}</button>
            </span>
          ) : (
            <button className="text-link text-link--danger" type="button" onClick={() => setConfirmForget(true)}>
              <Icon name="trash" className="icon icon--small" />
              {t('offline.forget.start')}
            </button>
          )}
        </footer>
      </section>
      <PoweredBy />
    </main>
  );
}
